import { useState } from "react";
import { ArrowCounterClockwise, Check, Checks, ThumbsUp } from "@phosphor-icons/react";
import type { AudienceCategory, RoomThread } from "../../shared/types.ts";
import { ProjectToggle } from "./ProjectToggle.tsx";
import { RoomThreadCard } from "./RoomThreadCard.tsx";
import type { ThreadActions } from "./ThreadRow.tsx";

/** Moderator thread card: shared card content with a horizontal icon action
 *  pair at stage-sized targets, likes inline in the meta line, and a confirm
 *  step on "Mark as answered" (an accidental press reorders the board). */
export function ThreadCard({ thread, categories, onAir, actions, queueVariant = false }: {
  thread: RoomThread;
  categories: AudienceCategory[];
  onAir: boolean;
  actions: ThreadActions;
  queueVariant?: boolean;
}) {
  const [confirmAnswer, setConfirmAnswer] = useState(false);

  const rail = queueVariant ? (
    <button type="button" className="btn good sm" data-testid="thread-approve-btn" onClick={() => actions.approve(thread)}>
      <Check weight="bold" /> Approve
    </button>
  ) : thread.status === "answered" ? (
    <button type="button" className="btn ghost sm" onClick={() => actions.reopen(thread)}>
      <ArrowCounterClockwise weight="bold" /> Reopen
    </button>
  ) : (
    <>
      <ProjectToggle
        onAir={onAir}
        canProject
        onProject={() => actions.project(thread)}
        onStop={actions.stopProjection}
        variant="icon"
      />
      <button
        type="button"
        className="btn icon thread-action-circle thread-action-answer"
        title="Mark answered"
        aria-label="Mark answered"
        data-testid="thread-mark-answered-btn"
        onClick={() => setConfirmAnswer(true)}
      >
        <Checks weight="bold" />
      </button>
    </>
  );

  return (
    <>
      <RoomThreadCard
        thread={thread}
        categories={categories}
        onAir={onAir}
        showStaleSource
        metaLike
        actionsRail={rail}
        showReplies
        renderReplyAction={(reply) => (
          <span className={`txt-btn like-btn moderator-like-count ${reply.likeCount > 0 ? "has-likes" : ""}`}>
            <ThumbsUp weight="bold" /> {reply.likeCount}
          </span>
        )}
      />
      {confirmAnswer && (
        <div
          className="confirm-scrim"
          data-testid="confirm-answer"
          onClick={() => setConfirmAnswer(false)}
        >
          <div className="confirm-panel" role="dialog" aria-modal="true" aria-label="Mark as answered?" onClick={(e) => e.stopPropagation()}>
            <h3>Mark as answered?</h3>
            <p className="confirm-body">“{truncate(thread.body)}”</p>
            <div className="confirm-actions">
              <button type="button" className="btn ghost" onClick={() => setConfirmAnswer(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn good"
                data-testid="confirm-answer-confirm"
                onClick={() => {
                  setConfirmAnswer(false);
                  actions.answer(thread);
                }}
              >
                <Checks weight="bold" /> Mark answered
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function truncate(text: string, max = 140): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}
